// Makes a background video/iframe "cover" its container (like object-fit: cover)
// regardless of the container's aspect ratio.
//
// The frame's real aspect ratio is resolved from the most reliable source
// available and cached, never re-derived from the frame's own (mutated)
// rendered size — that feedback loop was what left gaps when the embed was
// taller than the container.
//
// Ratio resolution order:
//   1. `data-video-ratio` (width/height) on the container — set from the embed
//      data in Twig, so it is exact.
//   2. `width`/`height` attributes on the frame.
//   3. `16 / 9` — the Vimeo background default.

const DEFAULT_RATIO = 16 / 9;

const resolveRatio = (container, frame) => {
    const explicit = parseFloat(container.getAttribute('data-video-ratio'));
    if (explicit > 0) return explicit;

    const attrW = parseFloat(frame.getAttribute('width'));
    const attrH = parseFloat(frame.getAttribute('height'));
    if (attrW > 0 && attrH > 0) return attrW / attrH;

    return DEFAULT_RATIO;
};

const fitFrame = (container) => {
    const selector = container.getAttribute('data-video-fit') || 'iframe';
    const frame = container.querySelector(selector);
    if (!frame) return;

    const w = container.clientWidth;
    const h = container.clientHeight;
    if (w === 0 || h === 0) return;

    const containerRatio = w / h;
    const frameRatio = resolveRatio(container, frame);

    // The container must clip the overflow and be a positioning context.
    if (getComputedStyle(container).position === 'static') {
        container.style.position = 'relative';
    }
    container.style.overflow = 'hidden';

    let frameW;
    let frameH;
    if (frameRatio > containerRatio) {
        // Frame is wider than the container → match height, overflow width.
        frameH = h;
        frameW = h * frameRatio;
    } else {
        // Frame is taller than the container → match width, overflow height.
        frameW = w;
        frameH = w / frameRatio;
    }

    frame.style.position = 'absolute';
    frame.style.top = '50%';
    frame.style.left = '50%';
    frame.style.width = `${frameW}px`;
    frame.style.height = `${frameH}px`;
    frame.style.maxWidth = 'none';
    frame.style.maxHeight = 'none';
    frame.style.transform = 'translate(-50%, -50%)';
};

const videoFit = () => {
    const containers = document.querySelectorAll('[data-video-fit]');
    if (!containers.length) return;

    const fitAll = () => containers.forEach(fitFrame);

    fitAll();

    // Re-fit whenever a container actually changes size (layout shifts, font
    // loading, orientation changes) — more reliable than window resize alone.
    if ('ResizeObserver' in window) {
        const observer = new ResizeObserver(fitAll);
        containers.forEach((container) => observer.observe(container));
    } else {
        window.addEventListener('resize', fitAll);
    }

    // The iframe/video can report its final size a moment after load.
    containers.forEach((container) => {
        const selector = container.getAttribute('data-video-fit') || 'iframe';
        const frame = container.querySelector(selector);
        if (frame) frame.addEventListener('load', () => fitFrame(container));
    });
};

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', videoFit);
} else {
    videoFit();
}

export default videoFit;
